import { useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { format } from "date-fns";
import { ArrowLeft, Receipt } from "@untitledui/icons";
import {
  learningGetPurchaseReceipt,
  useLearningGetSeatOrder,
} from "@/services/api/learning-purchases/learning-purchases";
import { Button } from "@/components/base/buttons/button";
import { useLearningPathsGetPublished } from "@/services/api/learning-paths/learning-paths";
import { fetchFeatureFlags } from "@/hooks/useFeatureFlags";
import {
  AWAITING_INVOICE_SEATS_MESSAGE,
  SESSION_SEATS_LAPSE_MESSAGE,
  formatSeatCount,
  seatOrderStatusLabel,
} from "@/features/seat-orders/seatOrders";
import { formatSessionDate, formatSessionTimes } from "@/features/instructor-led/iltSessions";
import { useRefreshAtCancellationCutOff } from "@/features/instructor-led/useRefreshAtCancellationCutOff";
import { SeatInvitesSection } from "@/features/seat-orders/components/SeatInvitesSection";
import { SeatOrderFiguresSection } from "@/features/seat-orders/components/SeatOrderFiguresSection";
import { TakeSeatSection } from "@/features/seat-orders/components/TakeSeatSection";
import { formatCoursePrice, toNum } from "@/utils/course-formatters";
import type { SeatOrderResponse } from "@/types/api";

export const Route = createFileRoute("/_app/my-purchases_/seat-orders/$seatOrderId")({
  // Seat orders are hidden while the payments or seat-orders flag is off (the API 404s them too).
  beforeLoad: async () => {
    const { payments, seatOrders } = await fetchFeatureFlags();
    if (!payments || !seatOrders) throw redirect({ to: "/my-purchases" });
  },
  component: SeatOrderPage,
});

/**
 * SeatOrderPage - One of the purchaser's seat orders (LMS-140): the course, when it was bought, how many seats at what
 * price each, and the amount paid, how many of its seats have been taken up, as counts only (LMS-148), and inviting
 * people to the seats by email (LMS-145). An order for seats on an instructor-led session (LMS-190) shows the session's date, time and location and
 * says that seats not taken up by the time it starts lapse; once it has started, they show as lapsed. Its seats are
 * handed out by invite too, and taken up as bookings on the session (LMS-191). An order paid by invoice (LMS-151) says
 * so, with its status (Awaiting invoice, Invoiced, Paid or Cancelled), its PO number and, once raised, its invoice
 * number; it has no receipt. An order a discount code paid for (LMS-187) says the code was applied and shows its £0
 * amount; it has no receipt either. The purchaser can take one of the seats for themselves (LMS-189). An invoice
 * order's seats can be shared, and taken, only once it has been invoiced: until then the page says so (LMS-195).
 */
function SeatOrderPage() {
  const { seatOrderId } = Route.useParams();
  const {
    data: order,
    isLoading,
    isError,
    refetch,
  } = useLearningGetSeatOrder(seatOrderId, {
    // Asked afresh on each visit: an ILT order's seats lapse when its session starts (LMS-190), and an admin may
    // move an invoice order on (LMS-151), so it is asked again when the tab regains focus too.
    query: { retry: false, staleTime: 0, refetchOnWindowFocus: true },
  });
  const {
    data: path,
    isError: isPathUnavailable,
    isFetching: isCheckingPath,
  } = useLearningPathsGetPublished(order?.learningPathId ?? "", {
    // Ownership of an order survives grant revocation; permission to open its path does not.
    query: {
      enabled: !!order?.learningPathId,
      retry: false,
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: "always",
    },
  });
  // Left open across the session's start: ask again then, so its unredeemed seats show as lapsed.
  useRefreshAtCancellationCutOff(
    order?.session && !order.session.hasStarted ? [order.session.startsAt] : [],
    () => void refetch(),
  );
  const [receiptPending, setReceiptPending] = useState(false);
  const [receiptError, setReceiptError] = useState(false);

  const handleViewReceipt = async (purchaseId: string) => {
    setReceiptError(false);
    setReceiptPending(true);
    try {
      const { receiptUrl } = await learningGetPurchaseReceipt(purchaseId);
      window.open(receiptUrl, "_blank", "noopener,noreferrer");
    } catch {
      setReceiptError(true);
    } finally {
      setReceiptPending(false);
    }
  };

  return (
    <main className="mx-auto max-w-[1280px] px-8 py-8">
      <Button color="link-gray" size="sm" href="/my-purchases" iconLeading={ArrowLeft}>
        My Purchases
      </Button>

      {isLoading ? (
        <div className="bg-arc-gray-100 mt-6 h-48 animate-pulse rounded-2xl" />
      ) : isError || !order ? (
        <div className="text-arc-error-600 py-8 text-center" role="alert">
          We couldn&apos;t find this order. It may not be yours, or it may no longer be available.
        </div>
      ) : (
        <>
          <h1 className="text-arc-gray-900 mt-6 mb-2 text-4xl font-semibold">Seat order</h1>
          <p className="text-arc-gray-600 mb-8 text-base">
            {order.session
              ? "The seats you bought on this session, for other people to take up."
              : order.learningPathId
                ? "The seats you bought on this learning path, for other people to take up."
                : "The seats you bought on this course, for other people to take up."}
          </p>

          <dl className="border-arc-gray-200 text-md grid max-w-xl grid-cols-[auto_1fr] gap-x-8 gap-y-4 rounded-2xl border p-6">
            <dt className="text-arc-gray-600 font-semibold">
              {order.learningPathId ? "Learning path" : "Course"}
            </dt>
            <dd className="text-arc-gray-900">
              {/* Only link after confirming the purchaser can still open the path (LMS-198). */}
              {order.learningPathId ? (
                path && !isPathUnavailable && !isCheckingPath ? (
                  <a
                    href={`/learning-paths/${order.learningPathId}`}
                    className="text-arc-primary-900 focus-visible:outline-arc-primary-500 rounded underline focus-visible:outline-2 focus-visible:outline-offset-2"
                  >
                    {order.courseTitle}
                  </a>
                ) : (
                  order.courseTitle
                )
              ) : order.courseId ? (
                <a
                  href={
                    order.session
                      ? `/face-to-face-training/${order.courseId}`
                      : `/courses/${order.courseId}`
                  }
                  className="text-arc-primary-900 focus-visible:outline-arc-primary-500 rounded underline focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  {order.courseTitle}
                </a>
              ) : (
                order.courseTitle
              )}
            </dd>
            {order.session && (
              <>
                <dt className="text-arc-gray-600 font-semibold">Session</dt>
                <dd className="text-arc-gray-900">{formatSessionDate(order.session)}</dd>
                <dt className="text-arc-gray-600 font-semibold">Time</dt>
                <dd className="text-arc-gray-900">{formatSessionTimes(order.session)} (UK time)</dd>
                <dt className="text-arc-gray-600 font-semibold">Location</dt>
                <dd className="text-arc-gray-900">
                  {order.session.venueName}
                  {order.session.venueAddress && `, ${order.session.venueAddress}`}
                </dd>
              </>
            )}
            <dt className="text-arc-gray-600 font-semibold">Date bought</dt>
            <dd className="text-arc-gray-900">
              {format(new Date(order.purchasedAt), "d MMMM yyyy")}
            </dd>
            {/* A full-comp discount code paid for the whole order (LMS-187), so its amount is £0. */}
            {order.paymentMethod === "DiscountCode" && (
              <>
                <dt className="text-arc-gray-600 font-semibold">Payment</dt>
                <dd className="text-arc-gray-900">Discount code applied</dd>
              </>
            )}
            {/* Seats on a free Restricted course (LMS-196): nothing to pay, so its amount is £0. */}
            {order.paymentMethod === "Free" && (
              <>
                <dt className="text-arc-gray-600 font-semibold">Payment</dt>
                <dd className="text-arc-gray-900">
                  {order.learningPathId ? "Free learning path" : "Free course"}, nothing to pay
                </dd>
              </>
            )}
            {/* The PO number the purchaser gave paying by card (LMS-193). */}
            {order.paymentMethod === "Card" && order.poNumber && (
              <>
                <dt className="text-arc-gray-600 font-semibold">PO number</dt>
                <dd className="text-arc-gray-900">{order.poNumber}</dd>
              </>
            )}
            {order.paymentMethod === "Invoice" && (
              <>
                <dt className="text-arc-gray-600 font-semibold">Payment</dt>
                <dd className="text-arc-gray-900">Pay by invoice</dd>
                <dt className="text-arc-gray-600 font-semibold">Status</dt>
                <dd className="text-arc-gray-900">{seatOrderStatusLabel(order.status)}</dd>
                <dt className="text-arc-gray-600 font-semibold">PO number</dt>
                <dd className="text-arc-gray-900">{order.poNumber || "None given"}</dd>
                {order.invoiceReference && (
                  <>
                    <dt className="text-arc-gray-600 font-semibold">Invoice number</dt>
                    <dd className="text-arc-gray-900">{order.invoiceReference}</dd>
                  </>
                )}
              </>
            )}
            <dt className="text-arc-gray-600 font-semibold">Seats bought</dt>
            <dd className="text-arc-gray-900">{formatSeatCount(toNum(order.seatCount))}</dd>
            {/* Seats an admin cancelled on an invoice order (LMS-195): they can't be taken up. */}
            {toNum(order.cancelledSeatCount) > 0 && (
              <>
                <dt className="text-arc-gray-600 font-semibold">Seats cancelled</dt>
                <dd className="text-arc-gray-900">
                  {formatSeatCount(toNum(order.cancelledSeatCount))}
                </dd>
              </>
            )}
            {order.session?.hasStarted && !order.session.isCancelled && (
              <>
                <dt className="text-arc-gray-600 font-semibold">Seats lapsed</dt>
                <dd className="text-arc-gray-900">
                  {formatSeatCount(toNum(order.lapsedSeatCount))}
                </dd>
              </>
            )}
            <dt className="text-arc-gray-600 font-semibold">Price per seat</dt>
            <dd className="text-arc-gray-900">
              {formatCoursePrice(order.seatPricePence, order.currency)}
            </dd>
            <dt className="text-arc-gray-600 font-semibold">Amount</dt>
            <dd className="text-arc-gray-900 font-semibold">
              {formatCoursePrice(order.amountPence, order.currency)}
            </dd>
          </dl>

          {/* An invoice order's seats can't be shared until its invoice has been raised (LMS-195). */}
          {order.status === "AwaitingInvoice" && (
            <p
              className="border-arc-warning-200 bg-arc-warning-50 text-arc-gray-900 text-md mt-6 max-w-xl rounded-lg border px-4 py-3"
              role="note"
            >
              {AWAITING_INVOICE_SEATS_MESSAGE}
            </p>
          )}

          {/* An invoice order an admin cancelled (LMS-151); one cancelled with its session says so below. */}
          {order.status === "Cancelled" && !order.session?.isCancelled && (
            <p
              className="border-arc-warning-200 bg-arc-warning-50 text-arc-gray-900 text-md mt-6 max-w-xl rounded-lg border px-4 py-3"
              role="note"
            >
              This order has been cancelled, so its seats can&apos;t be taken up and invites to them
              no longer work.
            </p>
          )}

          {order.session && (
            <p
              className="border-arc-warning-200 bg-arc-warning-50 text-arc-gray-900 text-md mt-6 max-w-xl rounded-lg border px-4 py-3"
              role="note"
            >
              {order.session.isCancelled
                ? cancelledMessage(order)
                : order.session.hasStarted
                  ? lapsedMessage(toNum(order.lapsedSeatCount), toNum(order.cancelledSeatCount))
                  : SESSION_SEATS_LAPSE_MESSAGE}
            </p>
          )}

          {/* A refunded order has no receipt to open (the API refuses one). */}
          {order.purchaseId && !order.isRefunded && (
            <div className="mt-6 flex flex-col items-start gap-2">
              <Button
                color="secondary"
                size="sm"
                iconLeading={Receipt}
                onClick={() => handleViewReceipt(order.purchaseId!)}
                isLoading={receiptPending}
                isDisabled={receiptPending}
              >
                View receipt
              </Button>
              {receiptError && (
                <span className="text-arc-error-600 text-sm" role="alert">
                  Receipt unavailable. Please try again later.
                </span>
              )}
            </div>
          )}

          <SeatOrderFiguresSection seatOrderId={order.seatOrderId} />

          {order.learningPathId ? (
            // Seats on a learning path are redeemed from LMS-199: until then they can't be handed out or taken.
            <p className="text-arc-gray-700 text-md mt-8 max-w-xl">
              You&apos;ll be able to invite people to these seats, or take one yourself, soon.
            </p>
          ) : (
            <>
              <TakeSeatSection order={order} />

              <SeatInvitesSection
                seatOrderId={order.seatOrderId}
                forSession={!!order.session}
                orderStatus={order.status}
              />
            </>
          )}
        </>
      )}
    </main>
  );
}

/**
 * An admin cancelled the order's session (LMS-192): the order is cancelled with it, and the
 * purchaser is told whether the card payment has been, or is being, refunded.
 */
function cancelledMessage(order: SeatOrderResponse) {
  const lead =
    "This session was cancelled by the organiser, so your order has been cancelled and invites to seats that weren't taken up no longer work.";
  const amount = formatCoursePrice(order.amountPence, order.currency);
  if (order.isRefunded) return `${lead} The ${amount} you paid has been refunded.`;
  if (order.refundDue) return `${lead} The ${amount} you paid is being refunded.`;
  if (order.purchaseId) return `${lead} The ${amount} you paid isn't refunded.`;
  return lead;
}

/**
 * Once the order's session has started (LMS-190): its unredeemed seats have lapsed, with no refund. Seats an admin
 * cancelled (LMS-195) neither lapsed nor were taken up.
 */
function lapsedMessage(lapsed: number, cancelled: number) {
  if (lapsed === 0)
    return cancelled > 0
      ? "This session has started. Every seat on this order that wasn't cancelled was taken up."
      : "This session has started. Every seat on this order was taken up.";
  return lapsed === 1
    ? "This session has started, so the 1 seat that wasn't taken up has lapsed. It isn't refunded."
    : `This session has started, so the ${lapsed} seats that weren't taken up have lapsed. They aren't refunded.`;
}
